/**
 * useAnimatedNumber — Conteo animado de un número (subida/bajada).
 *
 * QUÉ HACE: al montar cuenta desde `from` (0 por defecto) hasta el valor real,
 * y en cada cambio anima desde el valor mostrado hasta el nuevo. Devuelve la
 * dirección del cambio (`up`/`down`) para destellos de estado en KPIs.
 * QUÉ INVOCA: `subscribeFrame` (bucle rAF compartido), `useReducedMotion`.
 * QUÉ EXPORTA: useAnimatedNumber, UseAnimatedNumberOptions, AnimatedNumberResult.
 * QUÉ NO DEBE HACER: no formatea (el formateo lo decide el consumidor), no
 * conoce dominio ni datos de negocio.
 */
import { useEffect, useReducer, useRef, useState } from "react";
import {
  clamp01,
  EASE_OUT_CUBIC,
  lerp,
  MOTION,
  roundTo,
  subscribeFrame,
} from "@/lib/animation/motion";
import { useReducedMotion } from "./useReducedMotion";

/** Dirección del último cambio de valor. */
export type AnimatedNumberDirection = "up" | "down" | "flat";

export interface UseAnimatedNumberOptions {
  /** Duración de la transición en ms (por defecto `MOTION.series`). */
  duration?: number;
  /** Decimales a conservar en el valor devuelto (por defecto 0). */
  decimals?: number;
  /** Desactiva la animación (por defecto `true`). */
  enabled?: boolean;
  /** Valor inicial desde el que se cuenta al montar (por defecto 0). */
  from?: number;
}

export interface AnimatedNumberResult {
  /** Valor actual de la animación, redondeado a `decimals`. */
  value: number;
  /** Dirección del último cambio (`flat` si no cambió). */
  direction: AnimatedNumberDirection;
}

export function useAnimatedNumber(
  target: number,
  options: UseAnimatedNumberOptions = {},
): AnimatedNumberResult {
  const {
    duration = MOTION.series,
    decimals = 0,
    enabled = true,
    from = 0,
  } = options;
  const reducedMotion = useReducedMotion();
  const animate = enabled && !reducedMotion && Number.isFinite(target);
  const displayRef = useRef(animate ? from : target);
  const [, forceRender] = useReducer((count: number) => count + 1, 0);
  const [direction, setDirection] = useState<AnimatedNumberDirection>("flat");

  useEffect(() => {
    const previous = displayRef.current;
    if (!animate) {
      displayRef.current = target;
      setDirection(
        target > previous ? "up" : target < previous ? "down" : "flat",
      );
      return;
    }
    if (previous === target) return;

    setDirection(target > previous ? "up" : "down");
    const startTime = performance.now();
    let settleTimer: number | null = null;
    let done = false;

    const finish = (): void => {
      if (done) return;
      done = true;
      displayRef.current = target;
      unsubscribe();
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer);
        settleTimer = null;
      }
      forceRender();
    };

    const unsubscribe = subscribeFrame((now) => {
      const progress = EASE_OUT_CUBIC(clamp01((now - startTime) / duration));
      if (progress >= 1) {
        finish();
        return;
      }
      displayRef.current = lerp(previous, target, progress);
      forceRender();
    });

    /** Red de seguridad: fija el valor final si el rAF no llega a dispararse. */
    settleTimer = window.setTimeout(finish, duration + 200);

    return () => {
      unsubscribe();
      if (settleTimer !== null) window.clearTimeout(settleTimer);
    };
  }, [target, animate, duration, forceRender]);

  return { value: roundTo(displayRef.current, decimals), direction };
}
